import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { selectedClientServiceIdAtom } from "@/store";
import {
  NotificationsApi,
  NotificationsControllerGetNotificationsEventEnum,
  type NotificationUpdateCommand,
} from "../../config/apiClient";
import { createApiClient } from "../../config/createApiClient";
import { useWithTrackingMutation } from "./useWithTrackingMutation";

const apiClient = createApiClient(NotificationsApi);

interface QueryGetNotifications {
  page: number;
  size: number;
  receivedAt?: string;
}

export const useNotifications = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useGetNotifications = (params: QueryGetNotifications) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "notifications",
        params.page,
        params.size,
        params.receivedAt,
        selectedClientServiceIdAtomValue,
      ],
      enabled: selectedClientServiceIdAtomValue > 0,
      queryFn: () => {
        return apiClient.notificationsControllerGetNotifications({
          clientServiceId: selectedClientServiceIdAtomValue,
          page: params.page,
          size: params.size,
          receivedAt: params.receivedAt,
          event:
            NotificationsControllerGetNotificationsEventEnum.OrderLogCommentCreated,
        });
      },
    });

  const useGetLatestNotificationsByGroups = (params: QueryGetNotifications) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "notifications",
        params.page,
        params.size,
        params.receivedAt,
        selectedClientServiceIdAtomValue,
      ],
      enabled: selectedClientServiceIdAtomValue > 0,
      queryFn: () => {
        return apiClient.notificationsControllerGetLatestNotificationsByGroups({
          clientServiceId: selectedClientServiceIdAtomValue,
          page: params.page,
          size: params.size,
          receivedAt: params.receivedAt,
          event:
            NotificationsControllerGetNotificationsEventEnum.OrderLogCommentCreated,
        });
      },
    });

  const useUpdateNotification = () =>
    useWithTrackingMutation<
      {
        notificationId: string;
        notificationUpdateCommand: NotificationUpdateCommand;
      },
      Awaited<ReturnType<typeof apiClient.notificationsControllerUpdate>>
    >(
      (params) =>
        apiClient.notificationsControllerUpdate({
          clientServiceId: selectedClientServiceIdAtomValue,
          notificationUpdateCommand: params.notificationUpdateCommand,
          id: params.notificationId,
        }),
      [],
      {
        mutationKey: ["updateNotification"],
        onSuccess: () => {
          return queryClient.invalidateQueries({
            queryKey: ["notifications"],
          });
        },
      }
    );

  return {
    useGetNotifications,
    useGetLatestNotificationsByGroups,
    useUpdateNotification,
  };
};
